import { Modal, Stack, Text, Button, Center, Loader } from '@mantine/core';
import { useIntl } from 'react-intl';
import { useAppSelector } from '@store';
import { useNavigate } from 'react-router';
import routes from '@/commons/routes';
import { buildQueryFoldersSearchFromNotification } from '@/users/components/UserFolders/utils/queryFoldersState';
import { useEffect, useCallback, useState } from 'react';
import { useUser } from '@contexts/UserContext';
import { fetchFolderCategoryForNotification } from './fetchFolderCategory';

interface FilterSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  folderName: string;
  /** When set (e.g. parent already fetched category), skip GET /folders/:folderName. */
  categoryHint?: string;
}

export type FindMatchingFiltersContext = {
  folders?: any[];
  category?: string;
};

/**
 * Pick saved filters that apply to this folder's category.
 * Prefer `category` from a single-folder API response; otherwise resolve from `folders` in Redux.
 */
export const findMatchingFilters = (
  filters: any[],
  folderName: string,
  context: FindMatchingFiltersContext = {}
) => {
  const { folders = [], category: categoryFromHint } = context;

  const category =
    categoryFromHint !== undefined
      ? categoryFromHint
      : (() => {
          const folder = folders.find((f: any) => f.folderName === folderName);
          return folder?.category;
        })();

  if (category === undefined) {
    return [];
  }

  return filters.filter((filter: any) => {
    if (filter.category) {
      return filter.category === category;
    }
    return true;
  });
};

export const FilterSelectionModal = ({
  opened,
  onClose,
  folderName,
  categoryHint,
}: FilterSelectionModalProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { user } = useUser();
  const username = user?.username || '';

  const filters = useAppSelector((state) => state.filters.filters);

  const [folderCategory, setFolderCategory] = useState<string | null>(null);
  const [folderMetaLoading, setFolderMetaLoading] = useState(false);

  useEffect(() => {
    if (!opened || !folderName) {
      setFolderCategory(null);
      setFolderMetaLoading(false);
      return;
    }

    if (categoryHint !== undefined) {
      setFolderCategory(categoryHint);
      setFolderMetaLoading(false);
      return;
    }

    let cancelled = false;
    setFolderMetaLoading(true);
    setFolderCategory(null);

    (async () => {
      try {
        const cat = await fetchFolderCategoryForNotification(folderName);
        if (!cancelled) {
          setFolderCategory(cat);
        }
      } catch {
        if (!cancelled) {
          setFolderCategory(null);
        }
      } finally {
        if (!cancelled) {
          setFolderMetaLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [opened, folderName, categoryHint]);

  const availableFilters =
    folderCategory != null
      ? findMatchingFilters(filters, folderName, { category: folderCategory })
      : [];

  const handleOpenFilter = useCallback(
    (filterId: string) => {
      if (!username) {
        return;
      }

      const filterDef = filters.find((f: any) => f.id === filterId);
      const base = routes.routes.queryFolders.view(username, filterId);
      const qs = buildQueryFoldersSearchFromNotification(filterDef, folderName);
      navigate(qs ? `${base}?${qs}` : base);
      onClose();
    },
    [folderName, username, navigate, onClose, filters]
  );

  return (
    <Modal
      opened={opened}
      centered={true}
      onClose={onClose}
      title={intl.formatMessage({ id: 'selectFilter' })}
      size="md"
      withinPortal
      zIndex={1000}
    >
      <Stack gap="md">
        {folderMetaLoading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : !folderMetaLoading && availableFilters.length === 0 ? (
          <Center py="xl">
            <Text c="dimmed" ta="center">
              {intl.formatMessage({ id: 'noMatchingFilters' })}
            </Text>
          </Center>
        ) : (
          <Stack gap="md">
            {availableFilters.length > 1 && (
              <Text size="sm" c="dimmed">
                {intl.formatMessage({ id: 'folderFoundInMultipleFilters' })}
              </Text>
            )}
            <Stack gap="xs">
              {availableFilters.map((filter: any) => (
                <Button
                  key={filter.id}
                  variant="light"
                  fullWidth
                  justify="flex-start"
                  onClick={() => handleOpenFilter(filter.id)}
                >
                  {filter.name}
                </Button>
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};
